import { FormikContextType, useFormikContext } from 'formik';
import React from 'react';
import { RecurrenceConverters } from '@tableau/self-service-schedules';
import { ButtonWidget, TextLinkWidget } from '@tableau/widgets';
import { ServerUrls } from '../../../../app/ServerUrls';
import { UpdateScheduleFrequencyServerSettingNames } from '../../../../backgroundJob/types/UpdateScheduleFrequencyServerSettingRequest';
import { Messages } from '../../../../codegen/Localize';
import { FormikCheckbox } from '../../../../commonInfra/components/FormikCheckbox';
import { FormikSelfServiceSchedulePane } from '../../../../commonInfra/components/FormikSelfServiceSchedulePane';
import { openReactConfirmationDialog } from '../../../../commonInfra/dialogs/ReactConfirmationDialog';
import { TabDialogWidth } from '../../../../commonInfra/dialogs/style/TabDialogWidth';
import { HelpService } from '../../../../commonInfra/services/HelpService';
import { rejectIfResultContainsError } from '../../../../httpClient/utils/HttpErrorChecker';
import WebHttpClient from '../../../../httpClient/WebHttpClient';
import { L10n } from '../../../../ts/L10n/L10n';
import { RunActiveDirectoryGroupsSyncRequest } from '../../../../ts/ServerApi/requests/RunActiveDirectoryGroupsSyncRequest';
import { UpdateBooleanServerSettingNames } from '../../../../ts/ServerApi/requests/UpdateBooleanServerSettingRequest';
import { ToasterService, ToastType } from '../../../../ts/services/ToasterService';
import { SettingsSection } from '../../../components/common/SettingsSection';
import { IServerTimestamps, ServerSettingsInstance } from '../../../services/ServerSettings';
import { ServerSettingsUnauthenticated } from '../../../services/ServerSettingsUnauthenticated';
import { actionButtonContainer, learnMoreStyle, SettingsStyles, timestampStyle, viewActivityLinkContainer } from '../../../styles/CommonStyles';
import { ServerSettingsProps } from '../server-settings/ReactServerSettings';
import { componentRegistryEntry } from '../SettingsComponentRegistryFunctions';

export const AD_SYNC_SECTION_TEST_ID = 'ad-sync-server-setting';
export const AD_SYNC_LEARN_MORE_LINK_TEST_ID = 'scheduled-ad-sync-learn-more';
export const AD_SYNC_LAST_TIMESTAMP_TEST_ID = 'last-scheduled-ad-sync-time';
export const AD_SYNC_VIEW_ACTIVITY_LINK_TEST_ID = 'scheduled-ad-sync-activity';
export const AD_SYNC_ALL_GROUPS_BUTTON_TEST_ID = 'run-active-directory-groups-sync';
export const AD_SYNC_SCHEDULE_ENABLED_CHECKBOX_TEST_ID = 'schedule-ad-sync-button';
export const AD_SYNC_SCHEDULE_PICKER_LABEL_ID = 'ad-sync-schedule-picker-label';

export type ActiveDirectorySyncSectionProps = ServerSettingsProps<'scheduledActiveDirectorySyncEnabled' | 'activeDirectorySyncScheduleFrequency'>;

export function ActiveDirectorySyncSection(props: ActiveDirectorySyncSectionProps): JSX.Element {
  const formik = useFormikContext<ActiveDirectorySyncSectionProps>();

  if (!ServerSettingsUnauthenticated.isServerDomainFamilyActiveDirectory()) {
    return null;
  }

  return (
    <SettingsSection
      title={Messages.activeDirectorySyncSchedule()}
      description={getDescription()}
      testId={AD_SYNC_SECTION_TEST_ID}
    >
      {renderLastSyncTime(props.serverTimestamps)}
      {renderViewActivityLink()}
      {renderSyncAllGroupsButton()}
      {renderEnableScheduleCheckbox()}
      {renderSchedulePicker(formik)}
    </SettingsSection>
  );
}

function getDescription(): JSX.Element {
  return <>
    {Messages.activeDirectorySyncScheduleDetails()}
    <TextLinkWidget
      className={learnMoreStyle}
      href={HelpService.getServerHelpUrlByCategory('settings-adsync1')}
      target='_blank'
      testId={AD_SYNC_LEARN_MORE_LINK_TEST_ID}
    >
      {Messages.activeDirectorySyncScheduleLearnMoreLinkDescription()}
    </TextLinkWidget>
  </>;
}

function renderLastSyncTime(serverTimestamps: IServerTimestamps): JSX.Element {
  return <>
    {Messages.activeDirectorySyncScheduleLastSynchronized()}
    <span className={timestampStyle} data-tb-test-id={AD_SYNC_LAST_TIMESTAMP_TEST_ID}>
      {L10n.localizedDateTime(serverTimestamps.lastActiveDirectoryGroupsSyncTime)}
    </span>
    {Messages.activeDirectorySyncScheduleServerTimeLabel()}
  </>;
}

function renderViewActivityLink(): JSX.Element {
  return <div className={viewActivityLinkContainer}>
    <TextLinkWidget
      href={encodeURI(ServerUrls.getInstance().serverStatusVizUrl('BackgroundTasksforNonExtracts'))}
      target='_blank'
      testId={AD_SYNC_VIEW_ACTIVITY_LINK_TEST_ID}
    >
      {Messages.activeDirectorySyncScheduleViewUpdateLinkDescription()}
    </TextLinkWidget>
  </div>;
}

function renderSyncAllGroupsButton(): JSX.Element {
  return <div className={actionButtonContainer}>
    <ButtonWidget
      testId={AD_SYNC_ALL_GROUPS_BUTTON_TEST_ID}
      onClick={openConfirmationDialog}
    >
      {Messages.activeDirectorySyncScheduleRunNow_action()}
    </ButtonWidget>
  </div>;
}

function handleApiSuccess(): void {
  ToasterService.instance.pop({
    type: ToastType.Info,
    text: Messages.activeDirectorySyncScheduleRunNowSuccess()
  });
}

function handleApiError(): void {
  ToasterService.instance.pop({
    type: ToastType.Error,
    text: Messages.activeDirectorySyncScheduleRunNowError()
  });
}

function openConfirmationDialog(): void {
  openReactConfirmationDialog({
    title: Messages.activeDirectorySyncScheduleConfirmTitle(),
    message: Messages.activeDirectorySyncScheduleConfirmBody(),
    actionName: Messages.activeDirectorySyncScheduleConfirmButton(),
    action: () => WebHttpClient.sendRequest(new RunActiveDirectoryGroupsSyncRequest())
      .then(rejectIfResultContainsError)
      .then(handleApiSuccess, handleApiError),
    dialogWidth: TabDialogWidth.width375px,
    paramsForAction: null,
  });
}

function renderEnableScheduleCheckbox(): JSX.Element {
  return <div className={SettingsStyles.checkboxGroup}>
    <FormikCheckbox
      name='settings.scheduledActiveDirectorySyncEnabled'
      label={Messages.activeDirectorySyncScheduleEnabled()}
      testId={AD_SYNC_SCHEDULE_ENABLED_CHECKBOX_TEST_ID}
    />
  </div>;
}

function renderSchedulePicker(formik: FormikContextType<ActiveDirectorySyncSectionProps>): JSX.Element {
  if (!formik.values.settings.scheduledActiveDirectorySyncEnabled) {
    return null;
  }

  return (
    <FormikSelfServiceSchedulePane
      name='settings.activeDirectorySyncScheduleFrequency'
      label={Messages.frequency()}
      labelId={AD_SYNC_SCHEDULE_PICKER_LABEL_ID}
    />
  );
}

/* for registering the component in settings registry */
export const ActiveDirectorySyncSectionSpecs = componentRegistryEntry({
  name: 'ActiveDirectorySyncSection',
  component: ActiveDirectorySyncSection,
  onSubmit: async (settings) => {
    await Promise.all([
      ServerSettingsInstance.updateBoolean(
        UpdateBooleanServerSettingNames.scheduledActiveDirectorySyncEnabled,
        settings.scheduledActiveDirectorySyncEnabled),
      ServerSettingsInstance.updateScheduleFrequency(
        UpdateScheduleFrequencyServerSettingNames.activeDirectorySyncScheduleFrequency,
        settings.activeDirectorySyncScheduleFrequency)
    ]);
  },
  initialDataConversion: (settings) => {
    if (!settings.activeDirectorySyncScheduleFrequency) {
      return settings;
    }
    const patchedScheduleData = RecurrenceConverters.convertRecurrenceToLegacySchedule(
      RecurrenceConverters.castScheduleToRecurrence(settings.activeDirectorySyncScheduleFrequency)
    );
    delete patchedScheduleData.serverTimezoneOffset; // this offset is not used by server settings
    return {...settings, activeDirectorySyncScheduleFrequency: patchedScheduleData};
  }
});
